/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetWinnersRow, GetWinnerRow } from '../openapi-types.gen.js';

import type { MorfiEvent, RealtimeSpec } from '../../types/RealtimeSpec.js';
import { addD6String, HELPERS } from '../../helpers/index.js';
import { fromBinary } from '@bufbuild/protobuf';
import { decodeRealtimePayload } from '../../devtool/decode-realtime-payload.js';
import { AmmUserActivitySchema, MarketGraduatedNotificationSchema } from '@morfi/proto';
import { connectivityStore } from '../../core/connectivity-store.js';
import { _bumpPerf } from '../../devtool/perf-counters.js';
import { _observe } from '../../devtool/perf-histograms.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';
import type { PagedData } from '../../types/PagedData.js';


// ─── useGetWinners ────────────────────────────────────────────────────────────

export interface useGetWinnersArgs { readonly pubkeys?: string; readonly category?: string; readonly marketCapMin?: string; readonly marketCapMax?: string; readonly liquidityMin?: string; readonly liquidityMax?: string; readonly volumeMin?: string; readonly volumeMax?: string; readonly holdersMin?: string; readonly holdersMax?: string; readonly change24hMinBps?: string; readonly change24hMaxBps?: string; readonly graduatedAfter?: string; readonly graduatedBefore?: string; readonly sortBy?: string; readonly sortOrder?: string; readonly viewportGated?: boolean; }


async function _useGetWinnersQueryFn(
  args: useGetWinnersArgs,
  ctx: QueryFunctionContext,
  pageParam?: string | null,
  limitOverride?: number,
): Promise<PagedData<GetWinnersRow>> {
  const url = '/v1/winners';
  const params = new URLSearchParams();

  if (args.pubkeys !== undefined) params.set('pubkeys', String(args.pubkeys));
  if (args.category !== undefined) params.set('category', String(args.category));
  if (args.marketCapMin !== undefined) params.set('marketCapMin', String(args.marketCapMin));
  if (args.marketCapMax !== undefined) params.set('marketCapMax', String(args.marketCapMax));
  if (args.liquidityMin !== undefined) params.set('liquidityMin', String(args.liquidityMin));
  if (args.liquidityMax !== undefined) params.set('liquidityMax', String(args.liquidityMax));
  if (args.volumeMin !== undefined) params.set('volumeMin', String(args.volumeMin));
  if (args.volumeMax !== undefined) params.set('volumeMax', String(args.volumeMax));
  if (args.holdersMin !== undefined) params.set('holdersMin', String(args.holdersMin));
  if (args.holdersMax !== undefined) params.set('holdersMax', String(args.holdersMax));
  if (args.change24hMinBps !== undefined) params.set('change24hMinBps', String(args.change24hMinBps));
  if (args.change24hMaxBps !== undefined) params.set('change24hMaxBps', String(args.change24hMaxBps));
  if (args.graduatedAfter !== undefined) params.set('graduatedAfter', String(args.graduatedAfter));
  if (args.graduatedBefore !== undefined) params.set('graduatedBefore', String(args.graduatedBefore));
  if (args.sortBy !== undefined) params.set('sortBy', String(args.sortBy));
  if (args.sortOrder !== undefined) params.set('sortOrder', String(args.sortOrder));
  params.set('limit', String(limitOverride ?? 24));
  if (typeof pageParam === 'string' && pageParam.length > 0) {
    params.set('cursor', pageParam);
  }
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetWinners',
    routeTemplate: '/v1/winners',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as Record<string, unknown>;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const rows = (json['data'] ?? []) as readonly GetWinnersRow[];
  const nextCursorRaw = json['nextCursor'];
  const nextCursor =
    typeof nextCursorRaw === 'string' && nextCursorRaw.length > 0 ? nextCursorRaw : null;
  const hasMore = nextCursor !== null;
  return {
    data: rows,
    nextCursor,
    hasMore,
    ...(typeof json['virtualQuote'] === 'string' ? { virtualQuote: json['virtualQuote'] } : {}),
    ...(typeof json['supplyD6'] === 'string' ? { supplyD6: json['supplyD6'] } : {}),
    ...(typeof json['leftEdgeValue'] === 'string' ? { leftEdgeValue: json['leftEdgeValue'] } : {}),
    ...(typeof json['bucketSeconds'] === 'number' ? { bucketSeconds: json['bucketSeconds'] } : {}),
    
    ...(json['meta'] !== null && typeof json['meta'] === 'object' && typeof (json['meta'] as { rankingRevision?: unknown }).rankingRevision === 'string'
      ? { rankingRevision: (json['meta'] as { rankingRevision: string }).rankingRevision } : {}),
    ...(json['lastSeq'] !== null &&
    typeof json['lastSeq'] === 'object' &&
    (typeof (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === 'string' ||
      (json['lastSeq'] as { sequenceId?: unknown }).sequenceId === null)
      ? {
          snapshotSequence: (json['lastSeq'] as { sequenceId: string | null }).sequenceId,
        }
      : {}),
  };
}


const _useGetWinnersFactory = Object.assign(
  (args: useGetWinnersArgs) => ({
    queryKey: ['winners', 'list', { pubkeys: args.pubkeys ?? null, category: args.category ?? null, marketCapMin: args.marketCapMin ?? null, marketCapMax: args.marketCapMax ?? null, liquidityMin: args.liquidityMin ?? null, liquidityMax: args.liquidityMax ?? null, volumeMin: args.volumeMin ?? null, volumeMax: args.volumeMax ?? null, holdersMin: args.holdersMin ?? null, holdersMax: args.holdersMax ?? null, change24hMinBps: args.change24hMinBps ?? null, change24hMaxBps: args.change24hMaxBps ?? null, graduatedAfter: args.graduatedAfter ?? null, graduatedBefore: args.graduatedBefore ?? null, sortBy: args.sortBy ?? null, sortOrder: args.sortOrder ?? null }] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetWinnersQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetWinners',
      viewerSensitive: false,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['winners', 'list'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetWinnersArgs, PagedData<GetWinnersRow>>;


export const useGetWinnersSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/winners',
  method: 'GET' as const,
  kind: 'list' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { list: _useGetWinnersFactory },
  parseInput: (raw: unknown): useGetWinnersArgs => raw as useGetWinnersArgs,
  parseResponse: (raw: unknown): PagedData<GetWinnersRow> => raw as PagedData<GetWinnersRow>,
  specName: 'useGetWinners' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: true as const,
  // MOR-859 — normalized entity store. List caches own ordered ids and
  // pagination while each row lives once at the canonical entity key.
  normalized: true as const,
  entityQueryKey: (id: string) => ['outcomes', 'entity', id] as readonly unknown[],
  entityProjection: {"resource":"outcomes","fieldMap":{"pubkey":"market","outcomeLabel":"label","outcomeImageUrl":"imageUrl","outcomeImageNsfw":"imageNsfw","marketCap":"effectiveMarketCapD6","liquidity":"cpQuoteReserveD6"},"canonicalIdField":"pubkey","literals":{"graduated":true,"migrated":true}} as const,
} as unknown as HookSpec<JsonSerialisableArgs, PagedData<GetWinnersRow>>;
export const useGetWinnersPagination = {
  cursorParam: 'cursor',
  limitParam: 'limit',
  pageSize: 24,
  maxPageSize: 24,
  nextCursorField: 'nextCursor',
  mergeMode: 'append',
  /** Direct fetcher for fetchNextPage — bypasses the factory's queryFn so the
   *  caller can pass the page cursor without rebuilding the queryKey. */
  fetchPage: (
    args: useGetWinnersArgs,
    pageParam: string | null,
    options?: { readonly limit?: number; readonly signal?: AbortSignal },
  ) =>
    _useGetWinnersQueryFn(
      args,
      { signal: options?.signal } as unknown as QueryFunctionContext,
      pageParam,
      options?.limit,
    ),
} as const;



function _useGetWinnersPerEntitySubjects(_args: useGetWinnersArgs): readonly string[] {
  // Per-row wildcard observers + arg-substituted per-market subjects +
  // broadcast literals (prepend recipes). All register in the trie so
  // dispatch reaches the list's applyEvent / onSignal for every event family.
  const _subs: readonly string[] = ['morfi.amm.*.user.*.activity.v0', 'morfi.market.graduated.v0'];
  return [...new Set(_subs)];
}

function _useGetWinnersRowSubjectTemplates(_args: useGetWinnersArgs): readonly string[] {
  // Per-row wire templates. `useItemObserver` substitutes each visible
  // row's id into the `{rowKey}` placeholder ONLY — literal `*`
  // wildcard segments (the `actor` routing key) stay `*` on the wire.
  // The resulting bounded subject (e.g. `morfi.market.<pubkey>.user.*.
  // trade.v0`) is what `FilterManager` admits into `filter_subjects`.
  const _tmpls: readonly string[] = ['morfi.amm.{mint}.user.*.activity.v0'];
  return _tmpls;
}

function _useGetWinnersApplyEvent(
  prev: PagedData<GetWinnersRow> | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetWinnersArgs,
): PagedData<GetWinnersRow> | undefined {
  if (prev === undefined) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  // Walk rows on the envelope's `.data` array. Realtime patches preserve
  // pagination metadata (`nextCursor` / `hasMore`) — those move only
  // when fetchNextPage runs.
  const prevRows = prev.data;

  // One block per manifest subscription. First subject match wins.
  // morfi.amm.${mint}.user.*.activity.v0  (list-wide — subject is the route scope)
  {
    if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchAttempts');
    const _m = subject.match(/^morfi\.amm\.([^.]+)\.user\.[^.]+\.activity\.v0$/);
    if (_m !== null) {
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.subjectMatchHits');
      const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(AmmUserActivitySchema, _d)
            : fromBinary(AmmUserActivitySchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
      let _changed = false;
      const nextRows = prevRows.slice();
      for (let _i = 0; _i < nextRows.length; _i += 1) {
        const _rowOrig = nextRows[_i] as GetWinnersRow;
        let updated: GetWinnersRow = _rowOrig;
        updated = (HELPERS.applyAmmActivityToGraduatedRow as unknown as (prev: GetWinnersRow, raw: ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetWinnersRow)(updated, raw, args as unknown as Record<string, unknown>, _m[1]);
        if (updated !== _rowOrig) {
          nextRows[_i] = updated;
          _changed = true;
        }
      }
      if (!_changed) {
        if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.earlyReturns');
        return prev;
      }
      if (process.env.NODE_ENV !== 'production') _bumpPerf('applyEvent.mutations');
      return { ...prev, data: nextRows };
    }
  }

  // No subscription matched — leave cache untouched.
  return prev;
}

function _useGetWinnersOnEvent(
  args: useGetWinnersArgs,
  queryClient: { getQueryData: (k: readonly unknown[]) => unknown; setQueryData: (k: readonly unknown[], updater: (prev: unknown) => unknown) => void },
): (ev: MorfiEvent<unknown>) => void {
  const p0 = { pending: new Set<string>(), timer: null as ReturnType<typeof setTimeout> | null };
  return (ev: MorfiEvent<unknown>): void => {
    const subject = ev.subject;
    if (typeof subject !== 'string') return;
    // Each prepend branch decodes its own row var with ITS channel's
    // @morfi/proto schema (different literals may carry different
    // messages) — see emitDecodeRaw. No shared decode here.
      // morfi.market.graduated.v0
      if (/^morfi\.market\.graduated\.v0$/.test(subject)) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(MarketGraduatedNotificationSchema, _d)
            : fromBinary(MarketGraduatedNotificationSchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof MarketGraduatedNotificationSchema>>;
        const _id = (((((raw as Record<string, unknown> | undefined)?.['payload'] as Record<string, unknown> | undefined))?.['market']) ?? ((raw as Record<string, unknown> | undefined)?.['market']));
        if (typeof _id !== 'string' || _id.length === 0) return;
        p0.pending.add(_id);
        if (p0.timer === null) {
          p0.timer = setTimeout(() => {
            p0.timer = null;
            const ids = [...p0.pending];
            p0.pending.clear();
            if (ids.length === 0) return;
            const queryKey = _useGetWinnersFactory(args).queryKey;
            void (async () => {
              // Substitute every `{pathParam}` placeholder from the
              // route template with the caller's arg. Without this, the
              // literal string `{pubkey}` (etc.) ends up in the fetch
              // URL and the backend rejects it with
              // SYSTEM_VALIDATION_ERROR / 422 "Malformed request path".
              const url = `/v1/winners`;
              const params = new URLSearchParams();
              // Refresh the loaded window with the active filters and sort. The
              // returned array is the only ordering authority; the SDK never
              // ranks or inserts rows locally for this feed.
              if (args.pubkeys !== undefined) params.set('pubkeys', Array.isArray(args.pubkeys) ? args.pubkeys.join(',') : String(args.pubkeys));
              if (args.category !== undefined) params.set('category', Array.isArray(args.category) ? args.category.join(',') : String(args.category));
              if (args.marketCapMin !== undefined) params.set('marketCapMin', Array.isArray(args.marketCapMin) ? args.marketCapMin.join(',') : String(args.marketCapMin));
              if (args.marketCapMax !== undefined) params.set('marketCapMax', Array.isArray(args.marketCapMax) ? args.marketCapMax.join(',') : String(args.marketCapMax));
              if (args.liquidityMin !== undefined) params.set('liquidityMin', Array.isArray(args.liquidityMin) ? args.liquidityMin.join(',') : String(args.liquidityMin));
              if (args.liquidityMax !== undefined) params.set('liquidityMax', Array.isArray(args.liquidityMax) ? args.liquidityMax.join(',') : String(args.liquidityMax));
              if (args.volumeMin !== undefined) params.set('volumeMin', Array.isArray(args.volumeMin) ? args.volumeMin.join(',') : String(args.volumeMin));
              if (args.volumeMax !== undefined) params.set('volumeMax', Array.isArray(args.volumeMax) ? args.volumeMax.join(',') : String(args.volumeMax));
              if (args.holdersMin !== undefined) params.set('holdersMin', Array.isArray(args.holdersMin) ? args.holdersMin.join(',') : String(args.holdersMin));
              if (args.holdersMax !== undefined) params.set('holdersMax', Array.isArray(args.holdersMax) ? args.holdersMax.join(',') : String(args.holdersMax));
              if (args.change24hMinBps !== undefined) params.set('change24hMinBps', Array.isArray(args.change24hMinBps) ? args.change24hMinBps.join(',') : String(args.change24hMinBps));
              if (args.change24hMaxBps !== undefined) params.set('change24hMaxBps', Array.isArray(args.change24hMaxBps) ? args.change24hMaxBps.join(',') : String(args.change24hMaxBps));
              if (args.graduatedAfter !== undefined) params.set('graduatedAfter', Array.isArray(args.graduatedAfter) ? args.graduatedAfter.join(',') : String(args.graduatedAfter));
              if (args.graduatedBefore !== undefined) params.set('graduatedBefore', Array.isArray(args.graduatedBefore) ? args.graduatedBefore.join(',') : String(args.graduatedBefore));
              if (args.sortBy !== undefined) params.set('sortBy', Array.isArray(args.sortBy) ? args.sortBy.join(',') : String(args.sortBy));
              if (args.sortOrder !== undefined) params.set('sortOrder', Array.isArray(args.sortOrder) ? args.sortOrder.join(',') : String(args.sortOrder));
              const cached = queryClient.getQueryData(queryKey) as PagedData<GetWinnersRow> | undefined;
              const loadedCount = cached?.data.length ?? 24;
              params.set(
                'limit',
                String(Math.max(24, Math.min(loadedCount, 24))),
              );
              const fetched: GetWinnersRow[] = [];
              let json: { data?: ReadonlyArray<GetWinnersRow> | Record<string, GetWinnersRow>; nextCursor?: string | null };
              while (true) {
              const full = url + '?' + params.toString();
              const res = await fetchWithHttpTelemetry({
                operationName: 'useGetWinners.refresh',
                routeTemplate: '/v1/winners',
                requestKind: 'query',
                method: 'GET',
                url: full,
                init: {
                  method: 'GET',
                  credentials: 'include',
                  headers: { Accept: 'application/json' },
                },
              });
              if (!res.ok) {
                console.error('[morfi-sdk] useGetWinners refresh fetch failed:', res.status);
                return;
              }
              json = (await res.json()) as { data?: ReadonlyArray<GetWinnersRow> | Record<string, GetWinnersRow>; nextCursor?: string | null };
              // The list endpoint returns `data: [...]` (array) for
              // unfiltered queries but `data: { [id]: row }` when
              // filtered by id list. Handle both.
              const rawData = json.data;
              const pageRows = Array.isArray(rawData) ? rawData : Object.values(rawData ?? {});
              fetched.push(...pageRows);
              const cursor = json.nextCursor;
              if (fetched.length >= loadedCount || typeof cursor !== 'string' || cursor.length === 0 || pageRows.length === 0) break;
              params.set('cursor', cursor);
              params.set('limit', String(Math.min(loadedCount - fetched.length, 24)));
              }
              queryClient.setQueryData(queryKey, (prev: unknown) => {
                if (prev === undefined || prev === null) return prev;
                const pd = prev as PagedData<GetWinnersRow>;
                const nextCursor =
                  typeof json.nextCursor === 'string' && json.nextCursor.length > 0
                    ? json.nextCursor
                    : null;
                return {
                  ...pd,
                  data: fetched,
                  nextCursor,
                  hasMore: nextCursor !== null,
                } as PagedData<GetWinnersRow>;
              });
            })();
          }, 1000);
        }
      }
  };
}
export const useGetWinnersRealtime = {
  domain: 'winners' as const,
  domainPins: ['morfi.amm.*.user.*.activity.v0'] as readonly string[],
  rowKey: 'mint' as const,
  perEntitySubjects: (args: useGetWinnersArgs) => _useGetWinnersPerEntitySubjects(args),
  rowSubjectTemplates: (args: useGetWinnersArgs) => _useGetWinnersRowSubjectTemplates(args),
  viewportGated: false as const,
  singleton: false as const,
  applyEvent:
    (_args: useGetWinnersArgs) =>
    (prev: PagedData<GetWinnersRow> | undefined, ev: MorfiEvent<unknown>): PagedData<GetWinnersRow> | undefined =>
      _useGetWinnersApplyEvent(prev, ev, _args),
  onEvent: (args: useGetWinnersArgs, queryClient: { getQueryData: (k: readonly unknown[]) => unknown; setQueryData: (k: readonly unknown[], updater: (prev: unknown) => unknown) => void }) =>
    _useGetWinnersOnEvent(args, queryClient),
} as unknown as RealtimeSpec<JsonSerialisableArgs, PagedData<GetWinnersRow>, unknown>;


// ─── useGetWinner ────────────────────────────────────────────────────────────

export interface useGetWinnerArgs { readonly outcomeMint: string; }


async function _useGetWinnerQueryFn(
  args: useGetWinnerArgs,
  ctx: QueryFunctionContext,
): Promise<GetWinnerRow | null> {
  if (args.outcomeMint === undefined || args.outcomeMint === null || args.outcomeMint === '') return null;
  const url = `/v1/winners/${args.outcomeMint}`;
  const params = new URLSearchParams();
  // Fixed response DTO — no caller-provided field projection.
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetWinner',
    routeTemplate: '/v1/winners/{outcomeMint}',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetWinnerRow;
}


const _useGetWinnerFactory = Object.assign(
  (args: useGetWinnerArgs) => ({
    queryKey: ['outcomes', 'entity', args.outcomeMint] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetWinnerQueryFn(args, ctx),
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: (query: { state: { data: unknown; dataUpdatedAt: number } }) =>
      query.state.data === null ||
      typeof query.state.data !== 'object' ||
      !('parentMarket' in query.state.data) ||
      query.state.dataUpdatedAt < connectivityStore.getState().lastReconnectAt,
    refetchOnWindowFocus: false as const,
    refetchOnReconnect: false as const,
    retryOnMount: false as const,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: true,
      pollingManaged: true,
      specName: 'useGetWinner',
      viewerSensitive: false,
      persist: false,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: (id: string) => ['outcomes', 'entity', id] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetWinnerArgs, GetWinnerRow>;


export const useGetWinnerSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/winners/{outcomeMint}',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: true },
  entityKeys: { detail: _useGetWinnerFactory },
  parseInput: (raw: unknown): useGetWinnerArgs => raw as useGetWinnerArgs,
  parseResponse: (raw: unknown): GetWinnerRow => raw as GetWinnerRow,
  specName: 'useGetWinner' as const,
  polling: { hasNats: true } as const,
  viewerSensitive: false as const,
  nonpersistent: true as const,
  // MOR-859 — the detail hook reads and writes the same canonical entity as
  // every normalized market list. The completeness guard above still forces
  // a detail fetch when this key currently holds only the narrower list DTO.
  canonicalEntity: true as const,
  entityQueryKey: (id: string) => ['outcomes', 'entity', id] as readonly unknown[],
} as unknown as HookSpec<JsonSerialisableArgs, GetWinnerRow>;

function _useGetWinnerPerEntitySubjects(args: useGetWinnerArgs): readonly string[] {
  const subs: readonly string[] = [`morfi.amm.${args.outcomeMint}.user.*.activity.v0`];
  // An unfilled placeholder (hook called with an empty/undefined id)
  // interpolates to an empty subject token (morfi.market..user.*.trade.v0) or to
  // the JS stringification `'undefined'` / `'null'` / `'NaN'`. JetStream
  // either rejects the malformed subject (failing the WHOLE consumer
  // update) or accepts the sentinel-string token which never matches a
  // real publish. Filter all of these per-subject so a one-arg-missing
  // call site doesn't poison the consumer for every OTHER subject the
  // hook subscribes to.
  return subs.filter(
    (s) =>
      !s.includes('.undefined.') &&
      !s.endsWith('.undefined') &&
      !s.includes('.null.') &&
      !s.endsWith('.null') &&
      !s.includes('.NaN.') &&
      !s.endsWith('.NaN') &&
      !s.includes('..') &&
      !s.startsWith('.') &&
      !s.endsWith('.'),
  );
}

function _useGetWinnerApplyEvent(
  prev: GetWinnerRow | null | undefined,
  ev: MorfiEvent<unknown>,
  args: useGetWinnerArgs,
): GetWinnerRow | null | undefined {
  // A null row (e.g. a 401 → anonymous result) has nothing to patch.
  if (prev === undefined || prev === null) return prev;
  const subject = ev.subject;
  if (typeof subject !== 'string') return prev;
  // Wire bytes are decoded PER SUBJECT inside each matched dispatcher
  // block (each channel carries a different @morfi/proto message) —
  // see emitDecodeRaw. No shared top-level decode here.
  let next: GetWinnerRow = prev;
    // subject "morfi.amm.${mint}.user.*.activity.v0"
  {
    const _m = subject.match(/^morfi\.amm\.([^.]+)\.user\.[^.]+\.activity\.v0$/);
    if (_m !== null) {
        const _d = (ev as { readonly data?: unknown }).data;
        const raw = (_d instanceof Uint8Array
          ? process.env.NODE_ENV !== 'production'
            ? decodeRealtimePayload(AmmUserActivitySchema, _d)
            : fromBinary(AmmUserActivitySchema, _d)
          : _d) as ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>;
        next = (HELPERS.applyAmmActivityToWinner as unknown as (prev: GetWinnerRow, raw: ReturnType<typeof fromBinary<typeof AmmUserActivitySchema>>, args: Record<string, unknown>, subjectCapture?: string) => GetWinnerRow)(next, raw, args as unknown as Record<string, unknown>, _m[1]);
    }
  }
  return next;
}

export const useGetWinnerRealtime = {
  domain: 'winners' as const,
  domainPins: [] as readonly string[],
  rowKey: 'outcomeMint' as const,
  perEntitySubjects: (args: useGetWinnerArgs) => _useGetWinnerPerEntitySubjects(args),
  viewportGated: false as const,
  applyImmediately: true as const,
  admitSequenceOnlyOnMutation: true as const,
  singleton: true as const,
  applyEvent:
    (args: useGetWinnerArgs) =>
    (prev: GetWinnerRow | null | undefined, ev: MorfiEvent<unknown>): GetWinnerRow | null | undefined =>
      _useGetWinnerApplyEvent(prev, ev, args),
} as unknown as RealtimeSpec<JsonSerialisableArgs, GetWinnerRow, unknown>;
